// src/components/myteconstruction/OrganizationDashboard/ContactsCRM.js

import React, { useState, useEffect, useCallback } from 'react';
import ExcelUploadContactWizard from '../../ExcelUploadWizard/ExcelUploadContactWizard';
import ContactFormModal from './ContactFormModal';
import axios from 'axios';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function ContactsCRM() {
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [schemaMapping, setSchemaMapping] = useState(["Name", "Email", "Number"]);

  // Retrieve authToken from localStorage (or use context as needed)
  const authToken = localStorage.getItem('authToken');

  // Fetch contacts from the backend
  const fetchContacts = useCallback(async () => {
      try {
        setLoading(true);
        const resp = await axios.get('/api/myteconstruction/contacts', {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (resp.data.status === 'success') {
          setContacts(resp.data.data);
        } else {
          toast.error(resp.data.message || 'Error fetching contacts');
        }
      } catch (err) {
        console.error(err);
        toast.error('Server error fetching contacts');
      } finally {
        setLoading(false);
      }
    }, [authToken]);

    useEffect(() => {
      fetchContacts();
    }, [fetchContacts]);

  const handleUploadFinish = () => {
    setIsWizardOpen(false);
    fetchContacts();
  };

  // Helper: recursively flatten all fields (including nested ones)
  const getSearchableString = (contact) => {
    const flatten = (obj) => {
      let result = '';
      for (const key in obj) {
        if (!Object.prototype.hasOwnProperty.call(obj, key)) continue;
        const value = obj[key];
        if (value && typeof value === 'object') {
          result += ' ' + flatten(value);
        } else if (value) {
          result += ' ' + value;
        }
      }
      return result;
    };
    return flatten(contact).toLowerCase();
  };

  // Filter contacts based on a global search string (across all fields)
  const filteredContacts = contacts.filter(contact => {
    const searchTerm = search.toLowerCase();
    const contactString = getSearchableString(contact);
    return contactString.includes(searchTerm);
  });

  // Open the "Add Contact" modal
  const handleAddContact = () => {
    setIsContactModalOpen(true);
  };

  const handleCreateContact = async (formData) => {
    try {
      const resp = await axios.post('/api/myteconstruction/contacts', formData, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (resp.data.status === 'success') {
        toast.success('Contact created successfully');
        setIsContactModalOpen(false);
        fetchContacts();
      } else {
        toast.error(resp.data.message || 'Failed to create contact');
      }
    } catch (err) {
      console.error(err);
      toast.error('Server error creating contact');
    }
  };

  return (
    <div className="p-4 bg-gray-900 min-h-screen text-white">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">Contacts</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsWizardOpen(true)}
            className="bg-blue-600 px-3 py-2 rounded-md"
          >
            Upload Excel
          </button>
          <button
            onClick={handleAddContact}
            className="bg-green-600 px-3 py-2 rounded-md"
          >
            Add Contact
          </button>
        </div>
      </div>

      <div className="mb-4">
        <input
          className="bg-gray-800 px-3 py-2 rounded-md w-full text-sm"
          placeholder="Search contacts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="overflow-x-auto border border-gray-800 rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-800">
              <tr>
                <th className="p-2">Name</th>
                <th className="p-2">Email</th>
                <th className="p-2">Phone</th>
                <th className="p-2">Type</th>
                <th className="p-2">Company</th>
                <th className="p-2">Office</th>
                <th className="p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredContacts.map(contact => (
                <tr key={contact._id} className="border-b border-gray-700">
                  <td className="p-2">{contact.name}</td>
                  <td className="p-2">{contact.email}</td>
                  <td className="p-2">{contact.phone}</td>
                  <td className="p-2">{contact.contactType}</td>
                  <td className="p-2">
                    {contact.companyId && typeof contact.companyId === 'object'
                      ? contact.companyId.companyName
                      : contact.companyId || 'N/A'}
                  </td>
                  <td className="p-2">
                    {contact.officeId && typeof contact.officeId === 'object'
                      ? contact.officeId.officeName
                      : contact.officeId || 'N/A'}
                  </td>
                  <td className="p-2">
                    <button className="text-blue-400 hover:underline mr-2">Edit</button>
                    <button className="text-red-400 hover:underline">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isWizardOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 max-w-3xl w-full rounded p-4 border border-gray-700 relative">
            <ExcelUploadContactWizard
              userSchema={schemaMapping}
              setUserSchema={setSchemaMapping}
              onClose={() => setIsWizardOpen(false)}
              onSuccess={handleUploadFinish}
            />
          </div>
        </div>
      )}

      {isContactModalOpen && (
        <ContactFormModal
          onClose={() => setIsContactModalOpen(false)}
          onSubmit={handleCreateContact}
        />
      )}
    </div>
  );
}

export default ContactsCRM;
